/**
 * LetterTile — a large, tappable island tile showing one Arabic letter.
 *
 * States:
 *   locked       — greyed, subtle lock glyph, not clickable
 *   in_progress  — coral background, clickable
 *   completed    — leaf-green background, star count shown
 *
 * Typography notes:
 *   - Uses Noto Sans Arabic (font-body) for the big glyph, NOT Aref Ruqaa,
 *     because Aref Ruqaa's calligraphic dots sit very high above the
 *     baseline and get clipped on small tiles. Noto's dots are closer
 *     to the glyph and always render cleanly — critical for ت (2 dots)
 *     and ث (3 dots) which the user couldn't distinguish previously.
 *   - leading value is relaxed (1.2 instead of `leading-none`) so the
 *     top ascenders (dots) have breathing room.
 *   - Glyph is framed in a top-padded wrapper so even fonts with
 *     extra-tall dots don't bump the top edge.
 *
 * Touch target: 96×96 px minimum (well above the 60 pt minimum).
 */

interface Props {
  letter: string;
  nameAr: string;
  nameEn?: string;
  status: 'locked' | 'in_progress' | 'completed';
  stars: number;
  onClick?: () => void;
}

export function LetterTile({ letter, nameAr, nameEn, status, stars, onClick }: Props) {
  const bg = status === 'locked' ? 'bg-ink/15' : status === 'completed' ? 'bg-leaf' : 'bg-coral';
  const text = status === 'locked' ? 'text-ink/50' : 'text-white';
  const disabled = status === 'locked';

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      class={`relative flex flex-col items-center justify-between aspect-square rounded-[28px] px-2 pt-6 pb-2 ${bg} ${text}
              shadow-lg shadow-ink/10 transition-transform
              ${disabled ? 'cursor-not-allowed opacity-70' : 'active:scale-95 hover:scale-[1.02]'}`}
      aria-label={`${nameAr}${nameEn ? ` (${nameEn})` : ''}${disabled ? ' — locked' : ''}`}
    >
      {/* Letter — font-body (Noto Sans Arabic) because Aref Ruqaa's dots
          clip on small tiles. line-height: 1.2 lets the dots breathe. */}
      <span
        class="font-body font-bold select-none flex-1 flex items-center justify-center"
        style={{
          fontSize: 'clamp(44px, 13vw, 78px)',
          lineHeight: '1.2',
          paddingTop: '0.1em',
        }}
      >
        {letter}
      </span>

      {/* Arabic name — slightly bigger than before + Noto so it's legible */}
      <span class="text-[11px] font-body opacity-90 leading-none mt-1">
        {nameAr}
      </span>
      {nameEn && (
        <span
          class="text-[9px] font-body opacity-70 tracking-wide leading-none mt-0.5"
          dir="ltr"
        >
          {nameEn}
        </span>
      )}

      {status === 'completed' && (
        <span class="absolute top-2 right-2 text-xs bg-sun text-ink rounded-full px-2 py-0.5">
          ⭐ {stars}
        </span>
      )}
      {status === 'locked' && (
        <span class="absolute top-2 right-2 text-xl" aria-hidden="true">
          🔒
        </span>
      )}
    </button>
  );
}
